import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type {
  Payment, Checkout, LegalEntity, LegalEntityUpsert, PayfastSettings, PayfastSettingsUpsert,
  Receipt, BillingNotifications, BillingNotificationsUpsert, EmailStatus, EmailSenderUpsert,
} from '@arcnet/contracts';
import { billingKeys } from './hooks-billing.js';

interface ListResponse<T> { data: T[] }

export const paymentKeys = {
  list: (tenantId: string) => ['payments', tenantId] as const,
  receipt: (tenantId: string, receiptNumber: string) => ['receipt', tenantId, receiptNumber] as const,
  notifications: (tenantId: string) => ['billing-notifications', tenantId] as const,
  entity: () => ['billing-entity'] as const,
  payfast: () => ['billing-entity', 'payfast'] as const,
  email: (resellerId: string | null) => ['email-sender', resellerId ?? 'platform'] as const,
};

/** Waiting for PayFast's notification, and recent enough to be worth watching. */
export const isAwaiting = (p: Payment): boolean =>
  p.status === 'pending' && Date.now() - Date.parse(p.created_at) < 30 * 60_000;

export const usePayments = (tenantId: string | null) =>
  useQuery({
    queryKey: paymentKeys.list(tenantId ?? ''),
    queryFn: () => api.get<ListResponse<Payment>>(`/v1/tenants/${tenantId}/billing/payments`),
    enabled: !!tenantId,
    // The notification that completes a payment lands seconds after the payer
    // is sent back, so the list is watched while a payment is waiting.
    refetchInterval: (q) => (q.state.data?.data.some(isAwaiting) ? 4000 : false),
  });

/** When a payment completes, the balance and the ledger have moved with it. */
export function useBalanceFollowsPayments(tenantId: string | null, payments: Payment[] | undefined) {
  const qc = useQueryClient();
  const seen = useRef<number | null>(null);
  const completed = payments?.filter((p) => p.status === 'complete').length ?? 0;

  useEffect(() => {
    if (!payments || !tenantId) return;
    if (seen.current !== null && completed > seen.current) {
      void qc.invalidateQueries({ queryKey: billingKeys.billing(tenantId) });
      void qc.invalidateQueries({ queryKey: billingKeys.movements(tenantId) });
    }
    seen.current = completed;
  }, [payments, completed, tenantId, qc]);
}

export function useBuyAirtime(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (amount: string) =>
      api.post<Checkout>(`/v1/tenants/${tenantId}/billing/payments`, { amount }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: paymentKeys.list(tenantId) }); },
  });
}

export const useReceipt = (tenantId: string | null, receiptNumber: string | null) =>
  useQuery({
    queryKey: paymentKeys.receipt(tenantId ?? '', receiptNumber ?? ''),
    queryFn: () => api.get<Receipt>(
      `/v1/tenants/${tenantId}/billing/payments/${encodeURIComponent(receiptNumber ?? '')}`),
    enabled: !!tenantId && !!receiptNumber,
    retry: false, // a number that is not a receipt is a 404, and a real answer
  });

export const useBillingNotifications = (tenantId: string | null) =>
  useQuery({
    queryKey: paymentKeys.notifications(tenantId ?? ''),
    queryFn: () => api.get<BillingNotifications>(`/v1/tenants/${tenantId}/billing/notifications`),
    enabled: !!tenantId,
  });

export function useSaveBillingNotifications(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingNotificationsUpsert) =>
      api.put<BillingNotifications>(`/v1/tenants/${tenantId}/billing/notifications`, body),
    onSuccess: (saved) => {
      qc.setQueryData(paymentKeys.notifications(tenantId), saved);
      // The account shows the warning level too.
      void qc.invalidateQueries({ queryKey: billingKeys.billing(tenantId) });
    },
  });
}

export const useLegalEntity = () =>
  useQuery({
    queryKey: paymentKeys.entity(),
    queryFn: () => api.get<LegalEntity>('/v1/billing/entity'),
    retry: false, // none saved yet is a 404, and a real answer
  });

export function useSaveLegalEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LegalEntityUpsert) => api.put<LegalEntity>('/v1/billing/entity', body),
    onSuccess: (saved) => { qc.setQueryData(paymentKeys.entity(), saved); },
  });
}

export const usePayfastSettings = () =>
  useQuery({
    queryKey: paymentKeys.payfast(),
    queryFn: () => api.get<PayfastSettings>('/v1/billing/entity/payfast'),
    retry: false,
  });

export function useSavePayfastSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PayfastSettingsUpsert) =>
      api.put<PayfastSettings>('/v1/billing/entity/payfast', body),
    onSuccess: (saved) => { qc.setQueryData(paymentKeys.payfast(), saved); },
  });
}

/** ArcNet's own sender, or a reseller's. */
const senderPath = (resellerId: string | null): string =>
  resellerId ? `/v1/resellers/${resellerId}/email` : '/v1/billing/email';

export const useEmailStatus = (resellerId: string | null) =>
  useQuery({
    queryKey: paymentKeys.email(resellerId),
    queryFn: () => api.get<EmailStatus>(senderPath(resellerId)),
    retry: false,
  });

export function useSaveEmailSender(resellerId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmailSenderUpsert) => api.put<EmailStatus>(senderPath(resellerId), body),
    onSuccess: (saved) => { qc.setQueryData(paymentKeys.email(resellerId), saved); },
  });
}

export interface TestEmailResult { to: string; status: string; error: string | null }

export function useSendTestEmail(resellerId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<TestEmailResult>(`${senderPath(resellerId)}/test`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: paymentKeys.email(resellerId) }); },
  });
}
